import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import AppNavbar from '../../components/AppNavbar';
import SiteFooter from '../../components/SiteFooter';
import MobileBottomNav from '../../components/MobileBottomNav';


/* ─────────────────────── Status config ─────────────────────── */
const STATUS_CFG = {
  pending_payment:   { label: 'بانتظار الدفع',    dot: '#D97706', text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200'  },
  payment_submitted: { label: 'قيد المراجعة',     dot: '#2563EB', text: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-200'   },
  payment_confirmed: { label: 'تم التأكيد',       dot: '#16A34A', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  payment_rejected:  { label: 'رُفض الدفع',       dot: '#DC2626', text: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-200'    },
  ticket_issued:     { label: 'تم إصدار التذكرة', dot: '#16A34A', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  cancelled:         { label: 'ملغي',             dot: '#DC2626', text: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-200'    },
  completed:         { label: 'مكتمل',            dot: '#2563EB', text: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-200'   },
};

const TAB_FILTERS = [
  { key: 'all',       label: 'الكل',    statuses: null },
  { key: 'active',    label: 'نشطة',   statuses: ['pending_payment','payment_submitted','payment_confirmed','ticket_issued'] },
  { key: 'completed', label: 'مكتملة', statuses: ['completed'] },
  { key: 'cancelled', label: 'ملغية',  statuses: ['cancelled','payment_rejected'] },
];

/* ─────────────────────── Helpers ─────────────────────── */
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('ar-SA', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const fmtDateShort = (d) =>
  d ? new Date(d).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const fmtTime = (t) => {
  if (!t) return '—';
  if (/^\d{2}:\d{2}/.test(t)) return t.slice(0, 5);
  // ISO datetime → extract HH:MM (e.g. 2026-07-30T06:00:00.000Z → 06:00)
  const m = String(t).match(/T(\d{2}:\d{2})/);
  if (m) return m[1];
  return t;
};

const toArNum = (n) => String(n).replace(/[0-9]/g, d => '٠١٢٣٤٥٦٧٨٩'[d]);
const fmtPrice = (p) =>
  p ? toArNum(Number(p).toLocaleString('en-US')) + ' ج.س' : '—';

const parseSeats = (v) => {
  if (!v) return null;
  const join = (arr) => arr.map(n => toArNum(n)).join('، ');
  if (Array.isArray(v)) return join(v);
  try { const p = JSON.parse(v); return Array.isArray(p) ? join(p) : toArNum(v); }
  catch { return toArNum(v); }
};

/* ═══════════════════════════════════════════════════════════
   BookingStatusTabs
═══════════════════════════════════════════════════════════ */
const BookingStatusTabs = ({ active, onSelect, counts }) => (
  <div dir="rtl" className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
    {TAB_FILTERS.map(f => {
      const cnt = counts[f.key] ?? 0;
      const isActive = active === f.key;
      return (
        <button
          key={f.key}
          onClick={() => onSelect(f.key)}
          className={`flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-150 border ${
            isActive
              ? 'bg-[#1E40AF] text-white border-[#1E40AF] shadow-[0_2px_10px_rgba(30,64,175,.25)]'
              : 'bg-white text-gray-500 border-[#E5E7EB] hover:border-[#1E40AF]/40 hover:text-[#1E40AF]'
          }`}
        >
          {f.label}
          {cnt > 0 && (
            <span className={`text-[11px] font-bold px-1.5 py-px rounded-full min-w-[18px] text-center leading-5 ${
              isActive ? 'bg-white/20 text-white' : 'bg-[#EEF2FF] text-[#1E40AF]'
            }`}>
              {toArNum(cnt)}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   BookingFilterBar
═══════════════════════════════════════════════════════════ */
const BookingFilterBar = ({ search, onSearch, sort, onSort }) => (
  <div dir="rtl" className="flex flex-wrap items-center gap-3 mb-6">
    <div className="relative flex-1 min-w-[190px]">
      <svg className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
      </svg>
      <input
        type="text"
        value={search}
        onChange={e => onSearch(e.target.value)}
        placeholder="ابحث برقم الحجز أو المدينة..."
        className="w-full pr-9 pl-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:border-blue-400 transition-all"
      />
    </div>
    <select
      value={sort}
      onChange={e => onSort(e.target.value)}
      className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:border-blue-400 transition-all cursor-pointer"
    >
      <option value="date_desc">الأحدث أولاً</option>
      <option value="date_asc">الأقدم أولاً</option>
      <option value="price_desc">الأعلى سعراً</option>
      <option value="price_asc">الأقل سعراً</option>
    </select>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   SkeletonCard
═══════════════════════════════════════════════════════════ */
const SkeletonCard = () => (
  <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
    <div className="h-[3px] bg-gray-100" />
    <div className="px-5 py-4 flex items-center justify-between border-b border-gray-50">
      <div className="h-5 w-28 bg-gray-100 rounded-full animate-pulse" />
      <div className="h-4 w-20 bg-gray-100 rounded animate-pulse" />
    </div>
    <div className="px-5 py-5">
      <div className="flex items-center gap-4">
        <div className="space-y-2 flex-shrink-0">
          <div className="h-8 w-14 bg-gray-100 rounded animate-pulse" />
          <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
        </div>
        <div className="flex-1 space-y-2">
          <div className="h-1.5 bg-gray-100 rounded-full animate-pulse" />
          <div className="h-3 w-28 bg-gray-100 rounded mx-auto animate-pulse" />
        </div>
        <div className="space-y-2 flex-shrink-0">
          <div className="h-8 w-14 bg-gray-100 rounded animate-pulse" />
          <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
        </div>
      </div>
    </div>
    <div className="border-t border-dashed border-gray-100 mx-5" />
    <div className="px-5 py-4 flex items-center justify-between">
      <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
      <div className="h-9 w-32 bg-gray-100 rounded-xl animate-pulse" />
    </div>
  </div>
);

/* small spinner */
const SpinIcon = () => (
  <svg className="animate-spin" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83"/>
  </svg>
);

/* ═══════════════════════════════════════════════════════════
   BookingCard
═══════════════════════════════════════════════════════════ */
const BookingCard = ({ booking, index, onCancelled }) => {
  const [expanded,    setExpanded]    = useState(false);
  const [cancelling,  setCancelling]  = useState(false);
  const [downloading, setDownloading] = useState(false);

  const s   = booking.status || 'pending_payment';
  const cfg = STATUS_CFG[s] ?? STATUS_CFG.pending_payment;

  const canCancel   = ['pending_payment','payment_submitted','payment_confirmed'].includes(s);
  const canDownload = ['ticket_issued','completed'].includes(s);

  const seats    = parseSeats(booking.seatNumbers);
  const depCity  = booking.trip?.fromCity      || booking.fromCity      || '—';
  const arrCity  = booking.trip?.toCity        || booking.toCity        || '—';
  const depTime  = fmtTime(booking.trip?.departureTime || booking.departureTime);
  const arrTime  = fmtTime(booking.trip?.arrivalTime   || booking.arrivalTime);
  const tripDate = booking.trip?.tripDate || booking.tripDate || booking.trip?.departureTime || booking.departureTime;
  const company  = booking.trip?.company?.name || booking.companyName || '';
  const price    = booking.totalAmount || booking.amount || 0;

  const handleCancel = async () => {
    if (!window.confirm('هل أنت متأكد من إلغاء هذا الحجز؟')) return;
    setCancelling(true);
    try {
      await api.delete(`/bookings/${booking.id}/cancel`);
      onCancelled(booking.id);
    } catch { alert('تعذّر إلغاء الحجز. يرجى المحاولة مجدداً.'); }
    finally { setCancelling(false); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/bookings/${booking.id}/ticket`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a   = Object.assign(document.createElement('a'), {
        href: url,
        download: `تذكرة-${booking.bookingNumber || booking.id}.pdf`,
      });
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      let msg = 'تعذّر تنزيل التذكرة.';
      if (err.response?.data instanceof Blob) {
        try { const t = await err.response.data.text(); msg = JSON.parse(t).message || msg; } catch {}
      } else {
        msg = err.response?.data?.message || err.message || msg;
      }
      alert(msg);
    } finally { setDownloading(false); }
  };

  return (
    <article
      dir="rtl"
      className="bg-white rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(15,36,96,.07)] border border-gray-100 hover:shadow-[0_8px_28px_rgba(15,36,96,.12)] hover:-translate-y-[2px] transition-all duration-200"
      style={{ animation: 'cardSlideIn 0.22s ease both', animationDelay: `${index * 45}ms` }}
    >
      {/* Status accent top-bar */}
      <div className="h-[3px]" style={{ background: cfg.dot }} />

      {/* ── Header: status badge + booking# + booking date ── */}
      <div className="px-5 pt-4 pb-3.5 flex items-center justify-between gap-3 flex-wrap border-b border-gray-50">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-bold ${cfg.text} ${cfg.bg} ${cfg.border}`}>
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: cfg.dot }} />
            {cfg.label}
          </span>
          {(booking.bookingType === 'family' || booking.bookingType === 'group') && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-bold">
              {booking.bookingType === 'family' ? '👨‍👩‍👧‍👦 عائلي' : '👥 مجموعة'}
              {booking.groupName ? ` — ${booking.groupName}` : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2.5">
          {booking.bookingNumber && (
            <span dir="ltr" className="font-mono text-[11px] font-bold tracking-wide text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1">
              #{booking.bookingNumber}
            </span>
          )}
          {booking.createdAt && (
            <span className="text-[11px] text-gray-600 font-semibold hidden sm:block">
              {fmtDateShort(booking.createdAt)}
            </span>
          )}
        </div>
      </div>

      {/* ── Route ── */}
      <div className="px-5 pt-3 pb-4">
        {/* Company name */}
        {company && (
          <p className="text-[11px] font-semibold text-gray-500 mb-3 flex items-center gap-1.5">
            <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} className="text-gray-400 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16M3 21h18M9 21v-6h6v6"/>
            </svg>
            {company}
          </p>
        )}
        <div className="flex items-center">
          {/* Departure — right in RTL */}
          <div className="flex-shrink-0 text-right min-w-[88px]">
            <p className="text-[11px] font-bold text-gray-600 mb-1">مغادرة</p>
            <p className="font-mono text-[22px] font-black text-gray-900 leading-none tabular-nums">{toArNum(depTime)}</p>
            <p className="text-[14px] font-bold text-gray-800 mt-1.5">{depCity}</p>
          </div>

          {/* Journey connector */}
          <div className="flex-1 flex flex-col items-center gap-1 px-3">
            <div className="flex items-center w-full gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
              <div className="flex-1 border-t-[1.5px] border-dashed border-blue-200" />
              <span className="text-[17px] leading-none flex-shrink-0">🚌</span>
              <div className="flex-1 border-t-[1.5px] border-dashed border-blue-200" />
              <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
            </div>
            {tripDate && (
              <p className="text-[10px] text-blue-500 font-semibold text-center leading-none mt-1">
                {fmtDateShort(tripDate)}
              </p>
            )}
          </div>

          {/* Arrival — left in RTL */}
          <div className="flex-shrink-0 text-left min-w-[88px]">
            <p className="text-[11px] font-bold text-gray-600 mb-1">وصول</p>
            <p className="font-mono text-[22px] font-black text-gray-900 leading-none tabular-nums">{toArNum(arrTime)}</p>
            <p className="text-[14px] font-bold text-gray-800 mt-1.5">{arrCity}</p>
          </div>
        </div>
      </div>

      {/* ── Boarding-pass perforation ── */}
      <div className="relative mx-4">
        <span className="absolute -right-5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] rounded-full bg-[#F5F9FF]" />
        <span className="absolute -left-5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] rounded-full bg-[#F5F9FF]" />
        <div className="border-t-2 border-dashed border-gray-200" />
      </div>

      {/* ── Footer: seats + price + view details ── */}
      <div className="px-5 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {seats && (
            <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1">
              <span className="text-[12px]">💺</span>
              {seats}
            </span>
          )}
          {price > 0 && (
            <span className="font-black text-blue-700 font-mono tabular-nums text-[16px]">
              {fmtPrice(price)}
            </span>
          )}
        </div>

        <button
          onClick={() => setExpanded(o => !o)}
          className="inline-flex items-center gap-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 text-[13px] font-semibold px-3 py-1.5 rounded-xl transition-all border border-transparent hover:border-blue-100"
        >
          {expanded ? 'إخفاء' : 'التفاصيل'}
          <svg width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
            className={`transition-transform duration-200 ${expanded ? '-rotate-180' : ''}`}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
      </div>

      {/* ── Expanded: roster (family/group) + actions ── */}
      {expanded && (
        <div className="border-t border-gray-100 px-5 py-4 animate-slide-down">
          {Array.isArray(booking.passengerDetails) && booking.passengerDetails.length > 0 && (
            <div className="mb-4">
              <p className="text-[11px] font-bold text-gray-500 mb-2">
                {booking.bookingType === 'family' || booking.bookingType === 'group' ? 'أفراد الحجز' : 'المسافر'}
              </p>
              <div className="space-y-1.5">
                {booking.passengerDetails.map((pd, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-3 py-1.5 text-xs">
                    <span className="font-semibold text-gray-800">{pd.name || '—'}</span>
                    <span className="text-gray-500 flex items-center gap-2">
                      {pd.seat != null && <span dir="ltr">💺 {toArNum(pd.seat)}</span>}
                      {pd.phone && <span dir="ltr">{pd.phone}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 flex-wrap">
          {canDownload && (
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1E40AF] hover:bg-[#1D4ED8] active:scale-95 text-white text-sm font-bold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {downloading
                ? <SpinIcon />
                : <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M4 19h16"/>
                  </svg>
              }
              {downloading ? 'جارٍ التنزيل...' : 'تنزيل التذكرة PDF'}
            </button>
          )}
          {canCancel && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 active:scale-95 text-sm font-bold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {cancelling
                ? <SpinIcon />
                : <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
              }
              {cancelling ? 'جارٍ الإلغاء...' : 'إلغاء الحجز'}
            </button>
          )}
          {!canDownload && !canCancel && (
            <p className="text-sm text-gray-600 font-semibold">لا توجد إجراءات متاحة لهذا الحجز</p>
          )}
          </div>
        </div>
      )}
    </article>
  );
};

/* ═══════════════════════════════════════════════════════════
   GuestLookup
═══════════════════════════════════════════════════════════ */
const GuestLookup = ({ onFound }) => {
  const [phone,         setPhone]         = useState('');
  const [bookingNumber, setBookingNumber] = useState('');
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!phone.trim() && !bookingNumber.trim()) {
      setError('أدخل رقم الهاتف أو رقم الحجز للبحث');
      return;
    }
    setLoading(true); setError('');
    try {
      const params = {};
      if (phone.trim())         params.phone         = phone.trim();
      if (bookingNumber.trim()) params.bookingNumber = bookingNumber.trim();
      const res  = await api.get('/bookings/lookup', { params });
      const data = Array.isArray(res.data.data) ? res.data.data
                 : res.data.data ? [res.data.data] : [];
      if (data.length === 0) throw new Error('not_found');
      onFound(data);
    } catch {
      setError('لم يتم العثور على حجز. تحقق من البيانات المدخلة وأعد المحاولة.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-2xl shadow-[0_2px_16px_rgba(15,36,96,.08)] border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#2563EB" strokeWidth={2}>
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
          </div>
          <div>
            <h3 className="font-bold text-gray-800">البحث عن حجز</h3>
            <p className="text-xs text-gray-600 font-semibold mt-0.5">للضيوف — بحث بدون تسجيل دخول</p>
          </div>
        </div>
        <form onSubmit={handleLookup} className="space-y-3">
          <input
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="رقم الهاتف (مثال: 0912345678)"
            className="input-field text-right placeholder:text-right"
          />
          <input
            type="text"
            value={bookingNumber}
            onChange={e => setBookingNumber(e.target.value)}
            placeholder="رقم الحجز (اختياري)"
            className="input-field text-right placeholder:text-right font-mono tracking-wider"
          />
          {error && (
            <p className="text-red-600 text-sm font-medium flex items-center gap-1.5">
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} className="flex-shrink-0">
                <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" d="M12 8v4m0 4h.01"/>
              </svg>
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? <SpinIcon /> : (
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
            )}
            {loading ? 'جارٍ البحث...' : 'ابحث عن حجزي'}
          </button>
        </form>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PassengerBookingsPage  (main export)
═══════════════════════════════════════════════════════════ */
const PassengerBookingsPage = () => {
  const navigate   = useNavigate();
  const isLoggedIn = !!JSON.parse(localStorage.getItem('passenger_user') || 'null')?.id;

  const [bookings,   setBookings]   = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [activeTab,  setActiveTab]  = useState('all');
  const [search,     setSearch]     = useState('');
  const [sort,       setSort]       = useState('date_desc');
  const [showFilter, setShowFilter] = useState(false);
  const [guestMode,  setGuestMode]  = useState(!isLoggedIn);

  const fetchBookings = useCallback(async () => {
    if (!isLoggedIn) return;
    setLoading(true); setError('');
    try {
      const res = await api.get('/bookings/my');
      setBookings(Array.isArray(res.data.data) ? res.data.data : []);
    } catch {
      setError('تعذّر تحميل الحجوزات. يرجى المحاولة مجدداً.');
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const handleCancelled = (id) => setBookings(p => p.filter(b => b.id !== id));

  /* tab counts */
  const counts = TAB_FILTERS.reduce((acc, f) => {
    acc[f.key] = f.statuses
      ? bookings.filter(b => f.statuses.includes(b.status)).length
      : bookings.length;
    return acc;
  }, {});

  /* apply tab → search → sort */
  const filtered = bookings
    .filter(b => {
      const tf = TAB_FILTERS.find(f => f.key === activeTab);
      return tf?.statuses ? tf.statuses.includes(b.status) : true;
    })
    .filter(b => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        b.bookingNumber?.toLowerCase().includes(q) ||
        (b.trip?.departureCity || b.departureCity || '').toLowerCase().includes(q) ||
        (b.trip?.arrivalCity   || b.arrivalCity   || '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      if (sort === 'date_asc')   return new Date(a.createdAt) - new Date(b.createdAt);
      if (sort === 'price_desc') return (b.totalAmount || 0) - (a.totalAmount || 0);
      if (sort === 'price_asc')  return (a.totalAmount || 0) - (b.totalAmount || 0);
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

  const activeTabLabel = TAB_FILTERS.find(f => f.key === activeTab)?.label ?? 'الكل';

  return (
    <div dir="rtl" className="min-h-screen bg-[#F5F9FF] pb-16">
      <AppNavbar />

      {/* ════════════════════ Body ════════════════════ */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">

        {/* Page title row */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] border border-[#C7D2FE] flex items-center justify-center flex-shrink-0 text-xl">
              🎫
            </div>
            <div>
              <h1 className="text-[22px] font-black text-gray-900 leading-none">حجوزاتي</h1>
              <p className="text-gray-600 text-[13px] mt-0.5 font-semibold">
                {loading ? 'جارٍ التحميل...' : bookings.length === 0 ? 'لا توجد حجوزات' : `${toArNum(bookings.length)} حجز`}
              </p>
            </div>
          </div>
        </div>

        {/* Status filter tabs */}
        {!guestMode && (
          <div className="mb-5">
            <BookingStatusTabs active={activeTab} onSelect={setActiveTab} counts={counts} />
          </div>
        )}

        {/* Guest lookup */}
        {guestMode ? (
          <div>
            <GuestLookup onFound={(data) => { setBookings(data); setGuestMode(false); }} />
            {isLoggedIn && (
              <button
                onClick={() => setGuestMode(false)}
                className="mt-4 w-full text-center text-sm text-blue-600 hover:text-blue-800 font-semibold py-2 transition-colors"
              >
                ← عرض حجوزات حسابي
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-800 text-[15px]">
                  {activeTabLabel === 'الكل' ? 'جميع حجوزاتي' : activeTabLabel}
                </span>
                <span className="text-[11px] font-bold text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">
                  {toArNum(filtered.length)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFilter(o => !o)}
                  className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2 rounded-xl border transition-all ${
                    showFilter
                      ? 'bg-[#1E40AF] text-white border-[#1E40AF] shadow-sm'
                      : 'border-[#E5E7EB] bg-white text-gray-600 hover:border-blue-300 hover:text-blue-600'
                  }`}
                >
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M7 8h10M11 12h2"/>
                  </svg>
                  تصفية
                </button>
              </div>
            </div>

            {/* Filter bar */}
            {showFilter && (
              <BookingFilterBar
                search={search} onSearch={setSearch}
                sort={sort}     onSort={setSort}
              />
            )}

            {/* Error */}
            {error && (
              <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2.5 text-sm text-red-700">
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} className="flex-shrink-0">
                  <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" d="M12 8v4m0 4h.01"/>
                </svg>
                {error}
                <button onClick={fetchBookings} className="mr-auto text-xs font-bold underline hover:no-underline">
                  إعادة المحاولة
                </button>
              </div>
            )}

            {/* Skeletons */}
            {loading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
                {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
              </div>
            )}

            {/* Empty state */}
            {!loading && !error && filtered.length === 0 && (
              <div className="text-center py-20">
                <div className="text-7xl mb-5 opacity-70">🎫</div>
                <h3 className="text-gray-700 font-bold text-lg mb-2">لا توجد حجوزات</h3>
                <p className="text-gray-600 font-semibold text-sm mb-7 max-w-xs mx-auto">
                  {search || activeTab !== 'all'
                    ? 'لا توجد نتائج تطابق الفلاتر الحالية'
                    : 'لم تقم بإجراء أي حجز بعد. ابدأ بالبحث عن رحلة.'}
                </p>
                {search || activeTab !== 'all' ? (
                  <button
                    onClick={() => { setSearch(''); setActiveTab('all'); }}
                    className="border border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600 font-semibold px-5 py-2.5 rounded-xl text-sm transition-all"
                  >
                    مسح الفلاتر
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/')}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                    </svg>
                    ابحث عن رحلة
                  </button>
                )}
              </div>
            )}

            {/* Cards grid */}
            {!loading && filtered.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
                {filtered.map((b, i) => (
                  <BookingCard
                    key={b.id}
                    booking={b}
                    index={i}
                    onCancelled={handleCancelled}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <div className="hidden md:block"><SiteFooter navigate={navigate} /></div>
      <MobileBottomNav />
    </div>
  );
};

export default PassengerBookingsPage;
